import { Injectable } from "@angular/core";
import {
  catchError,
  from,
  map,
  mergeMap,
  of,
} from "rxjs";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import * as studentsActions from "./students.actions";
import * as invoicesActions from "../../office/invoice-ngrx/invoices.actions";
import * as paymentActions from "../../office/payment-ngrx/payments.actions";
import * as appointmentsActions from "../../calendar/ngrx/calendar.actions";
import * as settingsActions from "../../settings/ngrx/settings.action";
import * as errorActions from "../../core/error/ngrx/error.actions";
import { DataService } from "src/app/supabase/services/data.service";
import { FileService } from "src/app/supabase/services/files.service";
import { StudentDto } from "../interfaces/student_dto";

@Injectable()
export class StudentEffects {
  constructor(
    private actions$: Actions,
    private dataService: DataService,
    private fileService: FileService
  ) {}

  loadStudents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(studentsActions.loadStudents),
      mergeMap(() =>
        from(this.dataService.getStudents()).pipe(
          map((students) => studentsActions.loadStudentsSuccess({ students })),
          catchError((error) => of(errorActions.handleError({error: "Die Schüler konnten nicht geladen werden.", showToast: false, exception: error})))
        )
      )
    )
  );

  addStudent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(studentsActions.addStudent),
      mergeMap(({ student, profile_picture, attachments }) =>
        from(this.dataService.addStudent(student, profile_picture, attachments)).pipe(
          map((addedStudent: StudentDto) => studentsActions.addStudentSuccess({ student: addedStudent })),
          catchError((error) => of(errorActions.handleError({error: "Der Schüler konnte nicht erstellt werden.", exception: error})))
        )
      )
    )
  );

  updateStudent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(studentsActions.updateStudent),
      mergeMap(({ student, profile_picture, attachments }) =>
        from(this.dataService.updateStudent(student, profile_picture, attachments)).pipe(
          map((updatedStudent: StudentDto) => studentsActions.updateStudentSuccess({ student: updatedStudent })),
          catchError((error) => of(errorActions.handleError({error: "Der Schüler konnte nicht aktualisiert werden.", exception: error})))
        )
      )
    )
  );

  deleteStudent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(studentsActions.deleteStudent),
      mergeMap(({ student }) =>
        from(this.dataService.deleteStudent(student)).pipe(
          map((student) => studentsActions.deleteStudentSuccess({ student })),
          catchError((error) => of(errorActions.handleError({error: "Der Schüler konnte nicht gelöscht werden.", exception: error})))
        )
      )
    )
  );

  loadStudent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(studentsActions.loadStudent),
      mergeMap(({ studentId }) =>
        from(this.dataService.getStudent(studentId)).pipe(
          mergeMap((student) => [
            invoicesActions.upsertInvoices({ invoices: student.invoice || [] }),
            appointmentsActions.upsertAppointments({
              appointments: student.appointment,
            }),
            settingsActions.upsertLessonRatings({ lessonRatings: student.lesson_rating }),
            paymentActions.upsertPayments({ payments: student.payment }),
            // remove invoice and appointment from student
            studentsActions.loadStudentSuccess({student: {
              ...student,
              invoice: undefined,
              appointment: undefined,
              payment: undefined,
              lesson_rating: undefined,
            }}),
          ]),
          catchError((error) => of(errorActions.handleError({error: "Der Schüler konnte nicht geladen werden.", showToast: false, exception: error})))
        )
      )
    )
  );

  downloadAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(studentsActions.downloadAttachment),
      mergeMap(({ container, fileName, attachmentFileInfo }) =>
        from(this.fileService.downloadFile(container, attachmentFileInfo)).pipe(
          map((file) => studentsActions.openAttachment({ fileName, file })),
          catchError((error) => of(errorActions.handleError({error: "Die Datei konnte nicht heruntergeladen werden.", exception: error})))
        )
      )
    )
  );

  openAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(studentsActions.openAttachment),
      mergeMap(({ fileName, file }) =>
        from(this.fileService.openFile(fileName, file)).pipe(
          map(() => studentsActions.openAttachmentSuccess()), 
          catchError(() => of(studentsActions.openAttachmentFailed()))
        )
      )
    )
  );

  deleteAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(studentsActions.deleteAttachment),
      mergeMap(({ student, attachment }) =>
        from(this.dataService.deleteAttachment(student, attachment)).pipe(
          map(() => studentsActions.deleteAttachmentSuccess({ student, attachment })),
          catchError((error) => of(errorActions.handleError({error: "Die Datei konnte nicht gelöscht werden.", exception: error})))
        )
      )
    )
  )
}
